import ApiVideoPlayer, { ApiVideoPlayerRef } from "@api.video/react-player";
import { Slider } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import EditorialIcon from "../../assets/icons/card_editorial.svg?react";
import LikeIcon from "../../assets/icons/card_like.svg?react";
import LikeActiveIcon from "../../assets/icons/card_like_active.svg?react";
import SaveIcon from "../../assets/icons/card_save.svg?react";
import SaveActiveIcon from "../../assets/icons/card_save_active.svg?react";
import ShareIcon from "../../assets/icons/card_share.svg?react";
import ExternalLinkIcon from "../../assets/icons/external-link-pink.svg?react";
import PauseIcon from "../../assets/icons/pause-fill.svg?react";
import PlayIcon from "../../assets/icons/play-fill.svg?react";
import VolumeOffIcon from "../../assets/icons/video-player/volume-off.svg?react";
import VolumeOnIcon from "../../assets/icons/video-player/volume-on.svg?react";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useLikeVideo } from "../../domains/user/hooks/useLikeVideo";
import { useSaveVideo } from "../../domains/user/hooks/useSaveVideo";
import { useShareVideo } from "../../domains/user/hooks/useShareVideo";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventVideoPlay } from "../../tools/analytics/domains/video.analytics";
import { useVideoProgress } from "../../tools/analytics/useContentProgress";
import { isIOSApp } from "../../tools/ios";
import PlaylistPanel from "../app/PlaylistPanel";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import ProfilePicture from "../ProfilePicture";
import {
  resolveVideoPublisherDisplay,
  type VideoPublisherSource,
} from "../../domains/content/videoPublisher";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import { apiClient } from "../../services/api";

export default function VideoStoryScreen({
  video,
  height,
  analyticsListName,
  roomId,
}: {
  video: VideoPublisherSource;
  height: number | string;
  analyticsListName: AnalyticsItemList;
  /** The room the story is watched in, or null. A share keeps it. */
  roomId: string | null;
}) {
  const { currentUser } = useCurrentUser();
  const history = useHistory();
  const [isPlayerReady, setPlayerReady] = useState(false);
  const [isPlaylistsPanelVisible, setPlaylistsPanelVisible] = useState(false);
  const [hasPlayedOnce, setPlayedOnce] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [isMuted, setMuted] = useState(true);
  const [trackDuration, setTrackDuration] = useState(0);
  const [sliderTime, setSliderTime] = useState(0);
  const listName = AnalyticsItemList.STORY_LIST_PAGE;
  const { isLiked, toggleLike } = useLikeVideo(video, listName);
  const { isSaved, toggleSaveToPlaylists } = useSaveVideo(video, listName);
  const { triggerShare: shareVideo } = useShareVideo(listName);
  const playerRef = useRef<ApiVideoPlayerRef>(null);
  const videoProgress = useVideoProgress(video.id);
  const publisherData = useMemo(
    () => resolveVideoPublisherDisplay(video),
    [video]
  );
  const { videoStoryLink } = usePageLinks(null);

  useEffect(() => {
    if (isPlayerReady) {
      playerRef.current?.setCurrentTime(0);
      setPlaying(true);
      if (!isIOSApp()) setMuted(false);

      initialize();
    }
  }, [isPlayerReady]);

  useEffectOnce(() => {
    handleViewVideo();
  }, []);

  useEffect(() => {
    isPlaying ? playerRef.current?.play() : playerRef.current?.pause();
  }, [isPlaying]);

  useEffect(() => {
    isMuted ? playerRef.current?.mute() : playerRef.current?.unmute();
  }, [isMuted]);

  useEffect(() => {
    setPlaying(!isPlaylistsPanelVisible);
  }, [isPlaylistsPanelVisible]);

  async function initialize() {
    if (!(await isUserAuthentified())) return;
    try {
      await apiClient.post("/webapp/private/content/:id/views", {
        param: { id: video.id },
      });
      // TODO: Analytics (page doesn't exist in list).
    } catch {
      // api error already captured by onRequestError.
    }
  }

  function onPlayerReady() {
    setPlayerReady(true);
  }

  function handleViewVideo() {
    if (!hasPlayedOnce) {
      gaEventVideoPlay({
        video,
        listName: analyticsListName,
      });

      setPlayedOnce(true);
    }
  }

  function onTimeUpdate(time: number) {
    // NOTE: There is a bug here where this callback function
    // doesn't update props. So all videos are actually playing...
    setSliderTime(time);
    videoProgress.onPlayhead(time);
  }

  function handleClickWrapper() {
    if (isMuted) {
      setMuted(false);
      return;
    }

    setPlaying(!isPlaying);
  }

  function handleVisitLink() {
    history.replace(videoStoryLink(video));
  }

  return (
    <>
      <div className="box-border relative w-full bg-black" style={{ height }}>
        <div
          className="box-border bg-transparent absolute z-900! inset-0   "
          onClick={handleClickWrapper}
        />

        <div
          className={`absolute top-1/2 left-1/2 -translate-1/2  z-99 transition-opacity ease-out duration-300 [&_svg]:size-[54px]  ${isMuted ? "opacity-100" : "opacity-0"}`}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
        </div>

        <div className="absolute right-[10px] bottom-[126px] z-901! flex flex-col items-center gap-8 rtl:left-[10px] rtl:right-auto [&_img]:size-10  [&_.action-icon_svg]:size-10  [&_.publisher-logo]:box-border [&_.publisher-logo]:bg-white [&_.publisher-logo]:size-16  [&_.publisher-logo]:overflow-hidden [&_.publisher-logo]:rounded-[112px] [&_.publisher-logo]:p-[10px] [&_.publisher-logo_img]:size-full  [&_.publisher-logo_img]:object-contain [&_.publisher-logo.is-user]:p-0 [&_.publisher-logo.is-user_img]:object-cover">
          <div
            className={`publisher-logo ${
              publisherData.kind === "user" ? "is-user" : ""
            }`}
            onClick={() => {
              handleVisitLink();
              if (publisherData.path !== null) {
                history.push(publisherData.path);
              }
            }}
          >
            {publisherData.kind === "user" && publisherData.userProfile ? (
              <ProfilePicture
                user={publisherData.userProfile}
                height="100%"
                badge={null}
              />
            ) : publisherData.image ? (
              <img src={publisherData.image} alt="" />
            ) : null}
          </div>

          <div className="action-icon" onClick={() => toggleLike(video)}>
            {isLiked ? <LikeActiveIcon /> : <LikeIcon />}
          </div>
          <div
            className="action-icon"
            onClick={() => {
              setPlaylistsPanelVisible(true);
            }}
          >
            {isSaved ? <SaveActiveIcon /> : <SaveIcon />}
          </div>
          {currentUser ? (
            <div
              className="action-icon"
              onClick={() => shareVideo(video, currentUser, roomId)}
            >
              <ShareIcon />
            </div>
          ) : null}

          {(() => {
            const firstArticle = video.associatedArticles.at(0);
            if (firstArticle?.slug === undefined) return null;
            return (
              <Link
                onClick={() => {
                  handleVisitLink();
                }}
                to={"/post/" + firstArticle.slug}
              >
                <EditorialIcon />
              </Link>
            );
          })()}

          {video.externalLink !== null && (
            <a
              className="action-icon"
              target="_blank"
              href={video.externalLink}
              rel="noreferrer"
            >
              <ExternalLinkIcon />
            </a>
          )}
        </div>

        <ApiVideoPlayer
          ref={playerRef}
          video={{
            id:
              video.apiVideoId !== null
                ? video.apiVideoId
                : (() => {
                    throw new Error(
                      `Invariant: VideoStoryScreen requires apiVideoId, video ${video.id}`
                    );
                  })(),
          }}
          style={{
            width: "auto",
            height: "100%",
          }}
          videoStyleObjectFit={window.innerWidth > 550 ? "contain" : "cover"}
          chromeless
          autoplay
          muted={isMuted}
          onReady={onPlayerReady}
          // onFirstPlay={() => setStarted(true)}
          // The story stops on its last frame: the next tap plays it again.
          onEnded={() => {
            setPlaying(false);
          }}
          onTimeUpdate={onTimeUpdate}
          onDurationChange={(duration) => {
            setTrackDuration(duration);
            videoProgress.onDuration(duration);
          }}
        />

        <div className="absolute z-901! bottom-[3vh] inset-x-[28px]  bg-transparent box-border p-[16px_0] flex items-center gap-4 [&_.MuiSlider-root]:text-white [&_.MuiSlider-root]:w-[90%] [&_.MuiSlider-root]:rounded-none [&_.MuiSlider-root]:mx-auto [&_.MuiSlider-root]:block [&_.MuiSlider-root]:h-[3px] [&_.MuiSlider-rail]:text-white/30 [&_.MuiSlider-rail]:h-[3px] [&_.MuiSlider-track]:h-[3px] [&_.MuiSlider-track]:bg-white! [&_.MuiSlider-track]:border-white! [&_.MuiSlider-thumb]:text-white [&_.MuiSlider-thumb]:size-[22px]  [&_.MuiSlider-thumb]:shadow-none! [&_.MuiSlider-thumb.Mui-active]:shadow-[0_0_0_14px_rgba(255,255,255,0.16)]!">
          <Slider
            size="small"
            value={
              trackDuration && sliderTime
                ? (sliderTime / trackDuration) * 100
                : 0
            }
            onChange={(_, value) => {
              const numValue = value as number;
              const time = (numValue * trackDuration) / 100;
              playerRef.current?.setCurrentTime(time);
            }}
            onChangeCommitted={(_, value) => {
              const numValue = value as number;
              playerRef.current?.setCurrentTime(
                (numValue * trackDuration) / 100
              );
            }}
            step={2}
          />
          <div
            onClick={() => {
              setPlaying(!isPlaying);
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </div>
        </div>
      </div>

      <PlaylistPanel
        isVisible={isPlaylistsPanelVisible}
        contentToAdd={video}
        onClose={() => {
          setPlaylistsPanelVisible(false);
        }}
        onSave={async ({
          add,
          remove,
        }: {
          add: string[];
          remove: string[];
        }) => {
          await toggleSaveToPlaylists(video, { add, remove });
        }}
      />
    </>
  );
}
